import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { campaigns } from '@bb/contracts';
import { Checkbox, Field, Input, RadioGroup, Select } from '@bb/ui';
import { ApiError, type AdminApi } from '../../../api/client.ts';
import {
  formatSendTime,
  ISO_WEEKDAYS,
  scheduleIssues,
  switchScheduleKind,
  toggleWeekday,
  withFrequency,
  type Frequency,
  type RecurringSchedule,
  type ScheduledDelivery,
  type ScheduleKind,
} from './scheduledDelivery.ts';

export interface ScheduledDeliveryFormProps {
  environmentId: string;
  /** `content.schedule.scheduled` of the draft; no value yet reads as «As soon as the campaign launches». */
  value: ScheduledDelivery | undefined;
  /** Every edit, valid or not: the form shows what the contract rejects (see `scheduleIssues`). */
  onChange: (next: ScheduledDelivery) => void;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
  /** Debounce of the «Next send times» preview while the schedule is edited. */
  debounceMs?: number;
  /** What is scheduled, in the texts: a campaign sends, a Canvas enters users (BB-111). */
  objectLabel?: 'campaign' | 'Canvas';
  /** Why the form is read-only, shown under it. */
  readOnlyHint?: string;
}

const modes = (objectLabel: string): Array<{ value: ScheduleKind; label: string }> => [
  { value: 'immediate', label: `As soon as the ${objectLabel} launches` },
  { value: 'at', label: 'At a designated time' },
  { value: 'recurring', label: 'Recurring' },
];
const FREQUENCIES: Array<{ value: Frequency; label: string }> = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];
const intervalOptions = (every: Frequency) =>
  Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: i === 0 ? `1 ${every}` : `${i + 1} ${every}s` }));
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
const PREVIEW_COUNT = 5;

/** «Next send times» of the schedule, asked of the server once the value is valid and settles for `debounceMs`. */
function useSchedulePreview(environmentId: string, value: ScheduledDelivery, debounceMs: number) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState(key);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(key), debounceMs);
    return () => clearTimeout(timer);
  }, [key, debounceMs]);
  const scheduled = useMemo(() => {
    const parsed = campaigns.ScheduledDelivery.safeParse(JSON.parse(settled));
    return parsed.success && parsed.data.kind !== 'immediate' ? parsed.data : null;
  }, [settled]);
  return useQuery({
    queryKey: ['schedule-preview', environmentId, scheduled],
    queryFn: () => api.schedulePreview(environmentId, { scheduled: scheduled!, count: PREVIEW_COUNT }),
    enabled: scheduled !== null,
    placeholderData: keepPreviousData,
    retry: false,
  });
}

function LocalTimeChoice({ value, onChange, readOnly, oneOff, timeZone }: { value: boolean; onChange: (on: boolean) => void; readOnly: boolean; oneOff: boolean; timeZone: string | null }) {
  const zone = timeZone ? `${timeZone}, the environment time zone` : 'the environment time zone';
  return (
    <div>
      <Checkbox label="In users' local time zone" checked={value} disabled={readOnly} onCheckedChange={onChange} />
      <p className="hint">
        {oneOff
          ? 'Each user gets it at this time in their own time zone. Plan it at least 24 hours ahead: users in the earliest time zones get it first.'
          : 'Each user gets it at this time in their own time zone, starting with the earliest time zones.'}{' '}
        Without it, the time is in {zone}.
      </p>
    </div>
  );
}

function RecurringFields({ value, onChange, readOnly, issues, timeZone, objectLabel }: { value: RecurringSchedule; onChange: (next: RecurringSchedule) => void; readOnly: boolean; issues: Record<string, string>; timeZone: string | null; objectLabel: string }) {
  const day = value.dayOfMonth;
  return (
    <>
      <Field label="Frequency">
        <Select options={FREQUENCIES} value={value.every} disabled={readOnly} onChange={(e) => onChange(withFrequency(value, e.target.value as Frequency))} />
      </Field>
      <Field label="Every" error={issues.interval}>
        <Select options={intervalOptions(value.every)} value={String(value.interval)} disabled={readOnly} onChange={(e) => onChange({ ...value, interval: Number(e.target.value) })} />
      </Field>
      {value.every === 'week' && (
        <fieldset>
          <legend>Days of the week</legend>
          {ISO_WEEKDAYS.map(({ day: weekday, label }) => (
            <Checkbox key={weekday} label={label} checked={value.daysOfWeek?.includes(weekday) ?? false} disabled={readOnly} onCheckedChange={(on) => onChange(toggleWeekday(value, weekday, on))} />
          ))}
          {issues.daysOfWeek && (
            <p className="bb-field__error" role="alert">
              {issues.daysOfWeek}
            </p>
          )}
        </fieldset>
      )}
      {value.every === 'month' && (
        <Field label="Day of the month" hint={day !== undefined && day > 28 ? `${day} sends on the last day of shorter months.` : undefined} error={issues.dayOfMonth}>
          <Select options={DAYS_OF_MONTH} placeholder="Choose a day" value={day === undefined ? '' : String(day)} disabled={readOnly} onChange={(e) => onChange({ ...value, dayOfMonth: Number(e.target.value) })} />
        </Field>
      )}
      <Field label="Time" error={issues.time}>
        <Input type="time" value={value.time} disabled={readOnly} onChange={(e) => onChange({ ...value, time: e.target.value })} />
      </Field>
      <LocalTimeChoice value={value.localTime} onChange={(localTime) => onChange({ ...value, localTime })} readOnly={readOnly} oneOff={false} timeZone={timeZone} />
      <Field label="Starts on" error={issues.startDate}>
        <Input type="date" value={value.startDate} disabled={readOnly} onChange={(e) => onChange({ ...value, startDate: e.target.value })} />
      </Field>
      <Field label="Ends on (inclusive)" hint={`Optional: without it the ${objectLabel} repeats until it is stopped.`} error={issues.endDate}>
        <Input
          type="date"
          value={value.endDate ?? ''}
          disabled={readOnly}
          onChange={(e) => {
            const { endDate: _old, ...rest } = value;
            onChange(e.target.value ? { ...rest, endDate: e.target.value } : rest);
          }}
        />
      </Field>
    </>
  );
}

/**
 * When a scheduled campaign sends (BB-99): as soon as it launches, once at a designated time, or on a recurring
 * schedule; times are in the environment's time zone or in each user's own («local time», sent in waves). Checks
 * the contract while editing and lists the next send times the server plans for a valid schedule.
 */
export function ScheduledDeliveryForm({ environmentId, value, onChange, readOnly = false, debounceMs = 300, objectLabel = 'campaign', readOnlyHint = 'This revision is launched: its schedule cannot change.' }: ScheduledDeliveryFormProps) {
  const current: ScheduledDelivery = value ?? { kind: 'immediate' };
  const issues = scheduleIssues(current);
  const invalid = Object.keys(issues).length > 0;
  const preview = useSchedulePreview(environmentId, current, debounceMs);
  const data = preview.data;
  // The environment's zone, remembered for the local-time hint while the preview is off or failing.
  const [timeZone, setTimeZone] = useState<string | null>(null);
  useEffect(() => {
    if (data?.timeZone) setTimeZone(data.timeZone);
  }, [data?.timeZone]);

  let next;
  if (current.kind === 'immediate') next = <p>{objectLabel === 'Canvas' ? 'Users enter when the Canvas launches.' : 'Sends when the campaign launches.'}</p>;
  else if (invalid) next = <p className="hint">Fix the schedule to see the next send times.</p>;
  else if (preview.error)
    next = (
      <p className="bb-field__error" role="alert">
        {preview.error instanceof ApiError ? preview.error.message : 'The next send times could not be calculated.'}
      </p>
    );
  else if (!data) next = <p role="status">Calculating the next send times…</p>;
  else if (data.items.length === 0) next = <p>No upcoming sends.</p>;
  else
    next = (
      <>
        <p className="hint">
          Times in {data.timeZone}, the environment time zone.
          {data.items.some((r) => r.localTime) && ' A local-time send shows its first wave, in the earliest time zone.'}
        </p>
        <ol aria-label="Planned sends" aria-busy={preview.isPlaceholderData}>
          {data.items.map((run) => (
            <li key={run.runKey}>
              {formatSendTime(run.at, data.timeZone)}
              {run.localTime && ' (first wave)'}
            </li>
          ))}
        </ol>
      </>
    );

  return (
    <fieldset className="stack-form">
      <legend>{objectLabel === 'Canvas' ? 'Entry schedule' : 'Delivery schedule'}</legend>
      <RadioGroup
        label={objectLabel === 'Canvas' ? 'When users enter' : 'When to send'}
        options={modes(objectLabel).map((m) => ({ ...m, disabled: readOnly }))}
        value={current.kind}
        onValueChange={(kind) => onChange(switchScheduleKind(current, kind as ScheduleKind))}
      />
      {current.kind === 'at' && (
        <>
          <Field label="Date" error={issues.date}>
            <Input type="date" value={current.date} disabled={readOnly} onChange={(e) => onChange({ ...current, date: e.target.value })} />
          </Field>
          <Field label="Time" error={issues.time}>
            <Input type="time" value={current.time} disabled={readOnly} onChange={(e) => onChange({ ...current, time: e.target.value })} />
          </Field>
          <LocalTimeChoice value={current.localTime} onChange={(localTime) => onChange({ ...current, localTime })} readOnly={readOnly} oneOff timeZone={timeZone} />
        </>
      )}
      {current.kind === 'recurring' && <RecurringFields value={current} onChange={onChange} readOnly={readOnly} issues={issues} timeZone={timeZone} objectLabel={objectLabel} />}
      {readOnly && readOnlyHint && <p className="hint">{readOnlyHint}</p>}
      <section aria-label="Next send times">
        <h3>Next send times</h3>
        {next}
      </section>
    </fieldset>
  );
}
